"use client";

import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {useStyleContext, withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import {createToastHook} from "@gluestack-ui/toast";
import {AnimatePresence, Motion} from "@legendapp/motion";
import {cssInterop} from "nativewind";
import type {ComponentType} from "react";
import React, {useMemo} from "react";
import {AccessibilityInfo} from "react-native";

import {
    CheckedSolidCircleIcon,
    CloseIcon,
    InfoCircleSolidIcon
} from "../../../react/components/Icons";
import {Box} from "../box";
import {Text} from "../text";

const useToast = createToastHook(Motion.View, AnimatePresence);

export type IToastService = ReturnType<typeof useToast>;

const SCOPE = "TOAST";

cssInterop(
    Motion.View as ComponentType<{
        style: unknown;
    }>,
    {className: "style"}
);

const toastStyle = tva({
    base: "w-96 py-3 px-4 m-1 rounded-lg gap-2 web:pointer-events-auto shadow-hard-5",
    variants: {
        action: {
            error: "bg-error-500",
            warning: "bg-warning-500",
            success: "bg-success-600",
            info: "bg-white"
        },
        variant: {
            solid: "",
            outline: "border bg-background-0"
        }
    }
});

const toastTitleStyle = tva({
    base: "flex-1",
    parentVariants: {
        variant: {
            solid: "",
            outline: ""
        },
        action: {
            error: "color-white",
            warning: "color-white",
            success: "color-white",
            info: "color-blueGray-800"
        }
    }
});

const toastDescriptionStyle = tva({
    base: "",
    variants: {},
    parentVariants: {
        action: {
            error: "color-white",
            warning: "color-white",
            success: "color-white",
            info: "color-blueGray-800"
        }
    }
});

const Root = withStyleContext(Box, SCOPE);
export type IToastProps = React.ComponentProps<typeof Root> & {
    className?: string;
} & VariantProps<typeof toastStyle>;

const Toast = React.forwardRef<React.ElementRef<typeof Root>, IToastProps>(
    ({className, variant = "solid", action = "success", ...props}, ref) => {
        return (
            <Root
                ref={ref}
                className={toastStyle({variant, action, class: className})}
                context={{variant, action}}
                {...props}
            />
        );
    }
);

type IToastTitleProps = React.ComponentProps<typeof Text> & {
    className?: string;
} & VariantProps<typeof toastTitleStyle>;

const ToastTitle = React.forwardRef<React.ElementRef<typeof Text>, IToastTitleProps>(
    ({className, children, ...props}, ref) => {
        const {variant: parentVariant, action: parentAction} = useStyleContext(SCOPE);
        React.useEffect(() => {
            // Issue from react-native side
            // Hack for now, will fix this later
            AccessibilityInfo.announceForAccessibility(children as string);
        }, [children]);

        return (
            <Text
                {...props}
                ref={ref}
                aria-atomic="true"
                aria-live="assertive"
                className={toastTitleStyle({
                    class: className,
                    parentVariants: {
                        variant: parentVariant,
                        action: parentAction
                    }
                })}
                role="alert"
                variant="md"
            >
                {children}
            </Text>
        );
    }
);

type IToastDescriptionProps = React.ComponentProps<typeof Text> & {
    className?: string;
} & VariantProps<typeof toastDescriptionStyle>;

const ToastDescription = React.forwardRef<React.ElementRef<typeof Text>, IToastDescriptionProps>(
    ({className, ...props}, ref) => {
        const {action: parentAction} = useStyleContext(SCOPE);
        return (
            <Text
                ref={ref}
                {...props}
                className={toastDescriptionStyle({
                    class: className,
                    parentVariants: {
                        action: parentAction
                    }
                })}
            />
        );
    }
);

const iconStyle = tva({
    base: "",
    parentVariants: {
        action: {
            error: "fill-white",
            warning: "fill-white",
            success: "fill-white",
            info: "fill-blueGray-800"
        }
    }
});

const ToastIcon: React.FC = () => {
    const {action: parentAction} = useStyleContext(SCOPE);

    const Icon = useMemo(() => {
        if (parentAction === "error") {
            return CloseIcon;
        }
        if (parentAction === "warning") {
            return InfoCircleSolidIcon;
        }
        if (parentAction === "success") {
            return CheckedSolidCircleIcon;
        }
    }, [parentAction]);

    if (Icon) {
        return <Icon className={iconStyle({action: parentAction})} size="sm" />;
    }

    return null;
};

Toast.displayName = "Toast";
ToastTitle.displayName = "ToastTitle";
ToastDescription.displayName = "ToastDescription";

export {useToast as useToastUI, Toast, ToastTitle, ToastDescription, ToastIcon};
